import { Link } from "@/i18n/Link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, collectionPageJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    seoTitle: "Trailer Parts in Elkhart, IN - Dexter Axles, Hubs & Brakes",
    seoDesc: "Shop trailer parts in Elkhart, IN: Dexter axles, hubs, brakes, suspension, jacks, couplers, and wheels - in stock at the area's authorized Dexter distributor.",
    breadcrumbHome: "Home",
    breadcrumbProducts: "Products",
    collectionName: "Trailer Parts & Components",
    collectionDesc: "Trailer axles, hubs, brakes, suspension, jacks, couplers, and wheels from the Dexter Group.",
    categoryLabel: "Products",
    heroTitle: "Trailer parts & components.",
    heroDesc: "We stock the full Dexter line plus genuine replacement parts for nearly every trailer on the road. Browse by category - or order online any time.",
    shopOnline: "Open online store",
    categories: [
      { title: "Hubs, Drums & Brakes", desc: "1,000–10,000 lb capacities. Electric drum, hydraulic drum, and hydraulic disc.", count: "Dozens of SKUs" },
      { title: "Suspension Parts", desc: "Springs, hangers, equalizers, shackles, hardware. Double-eye and slipper systems.", count: "Full Dexter line" },
      { title: "Jacks & Couplers", desc: "Tongue jacks 800–10,000 lb, A-frame couplers, surge actuators, and accessories.", count: "All capacities" },
      { title: "Tires & Wheels", desc: "8-inch through 17.5-inch trailer tires and wheels. Steel, aluminum, chrome, galvanized.", count: "Sizes 8–17.5\"" },
    ],
    explore: "Explore",
    specPartTitle: "Looking for a specific part?",
    specPartDesc: "Our online store has live inventory and direct shipping. Or call the shop and we'll spec the right part for your application.",
    shopOnlineBtn: "Shop online",
    talkToPerson: "Talk to a person"
  },
  es: {
    seoTitle: "Repuestos para Remolques en Elkhart, IN - Ejes, Mazas y Frenos Dexter",
    seoDesc: "Compre repuestos para remolques en Elkhart, IN: ejes, mazas, frenos, suspensión, gatos, acopladores y ruedas Dexter - en existencia en el distribuidor autorizado de Dexter en el área.",
    breadcrumbHome: "Inicio",
    breadcrumbProducts: "Productos",
    collectionName: "Repuestos y Componentes para Remolques",
    collectionDesc: "Ejes para remolques, mazas, frenos, suspensión, gatos, acopladores y ruedas de Dexter Group.",
    categoryLabel: "Productos",
    heroTitle: "Repuestos y componentes para remolques.",
    heroDesc: "Contamos con toda la línea Dexter además de repuestos genuinos para casi cualquier remolque en carretera. Explore por categoría o compre en línea en cualquier momento.",
    shopOnline: "Abrir tienda en línea (en inglés)",
    categories: [
      { title: "Mazas, Tambores y Frenos", desc: "Capacidades de 1,000 a 10,000 lb. Tambor eléctrico, tambor hidráulico y disco hidráulico.", count: "Decenas de artículos" },
      { title: "Piezas de Suspensión", desc: "Ballestas, soportes, ecualizadores, grilletes y herrajes. Sistemas de doble ojo y deslizantes.", count: "Línea completa Dexter" },
      { title: "Gatos y Acopladores", desc: "Gatos de lengüeta de 800–10,000 lb, acopladores tipo A, actuadores de inercia y accesorios.", count: "Todas las capacidades" },
      { title: "Llantas y Ruedas", desc: "Llantas y ruedas para remolque de 8 a 17.5 pulgadas. Acero, aluminio, cromo, galvanizado.", count: "Medidas 8–17.5\"" },
    ],
    explore: "Explorar",
    specPartTitle: "¿Busca una pieza específica?",
    specPartDesc: "Nuestra tienda en línea tiene inventario en tiempo real y envío directo. O llame al taller y especificaremos la pieza correcta para su aplicación.",
    shopOnlineBtn: "Comprar en línea (en inglés)",
    talkToPerson: "Hablar con alguien"
  }
};

export default function ProductsIndex() {
  const t = useCopy(COPY);
  const trail = [
    { name: t.breadcrumbHome, path: "/" },
    { name: t.breadcrumbProducts, path: "/products/" },
  ];

  const CATEGORIES_DATA = [
    { path: "/products/hubs/", data: t.categories[0] },
    { path: "/products/suspension-parts/", data: t.categories[1] },
    { path: "/products/jacks-couplers/", data: t.categories[2] },
    { path: "/products/tires-wheels/", data: t.categories[3] },
  ];

  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/products/"
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          collectionPageJsonLd({
            name: t.collectionName,
            description: t.collectionDesc,
            path: "/products/",
            items: CATEGORIES_DATA.map((c) => ({ name: c.data.title, path: c.path, description: c.data.desc })),
          }),
        ]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.categoryLabel}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.heroTitle}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.heroDesc}
          </p>
          <div className="mt-8">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">{t.shopOnline} <ArrowRight className="ml-1.5 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <div className="grid gap-4 md:grid-cols-2">
          {CATEGORIES_DATA.map((c) => (
            <Link
              key={c.path}
              to={c.path}
              className="group flex flex-col justify-between p-7 bg-card border border-border rounded-md hover:border-primary hover:shadow-md transition-all"
            >
              <div>
                <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">{c.data.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{c.data.desc}</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.data.count}</span>
                <span className="text-sm font-medium text-primary-strong inline-flex items-center">
                  {t.explore} <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 grid md:grid-cols-2 items-center gap-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{t.specPartTitle}</h2>
            <p className="mt-3 text-secondary-foreground/80 max-w-xl leading-relaxed">
              {t.specPartDesc}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="https://parts.axleinc.com/" target="_blank" rel="noopener">{t.shopOnlineBtn} <ArrowRight className="ml-1.5 h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <Link to="/contact/">{t.talkToPerson}</Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
